import { createStyles, Group, Stack } from '@mantine/core';
import { IconButton, Text, Title } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import type { IApiListResponse, ITag } from '../../../../api';
import type { AiOutputBlockStore } from '../../store';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { Preview } from './Preview';

interface AiSettingModalRightColumnContentProps {
	store: AiOutputBlockStore;
	resourceCount: number;
	setPreviewIndex: (index: number) => void;
	tagsData?: IApiListResponse<ITag>;
	onFeedbackSubmit: (sentiment: 'positive' | 'negative') => Promise<void>;
	isSubmittingFeedback: boolean;
}

const useStyles = createStyles((theme) => ({
	headerWrapper: {
		padding: `${theme.spacing.md} ${theme.spacing.xl}`,
		justifyContent: 'space-between',
		borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
	},
	headerNavigationWrapper: {
		padding: `0 ${theme.spacing.sm}`,
		gap: theme.spacing['3xs'],
		flexWrap: 'nowrap',
	},
	headerNavigationControls: {
		gap: theme.spacing['3xs'],
		flexWrap: 'nowrap',
	},
	contentWrapper: {
		padding: `${theme.spacing.md} ${theme.spacing.xl}`,
		overflow: 'auto',
	},
	footerWrapper: {
		padding: `${theme.spacing.md} ${theme.spacing.xl}`,
		justifyContent: 'space-between',
		borderTop: `1px solid ${theme.other.getColor('border/secondary/default')}`,
	},
	footerFeedbackControls: {
		gap: theme.spacing['3xs'],
		flexWrap: 'nowrap',
	},
}));

export const AiSettingModalRightColumnContent = observer(
	({
		store,
		resourceCount,
		setPreviewIndex,
		tagsData,
		onFeedbackSubmit,
		isSubmittingFeedback,
	}: AiSettingModalRightColumnContentProps) => {
		const { classes } = useStyles();

		const decrementIndex = useCallback(() => {
			setPreviewIndex(store.previewIndex - 1);
		}, [store.previewIndex, setPreviewIndex]);

		const incrementIndex = useCallback(() => {
			setPreviewIndex(store.previewIndex + 1);
		}, [store.previewIndex, setPreviewIndex]);

		const handlePositiveFeedback = useCallback(
			() => onFeedbackSubmit('positive'),
			[onFeedbackSubmit]
		);
		const handleNegativeFeedback = useCallback(
			() => onFeedbackSubmit('negative'),
			[onFeedbackSubmit]
		);

		const currentFeedback = store.getCurrentFeedback();
		const hasSubmittedFeedback = store.hasCurrentFeedback();
		const isButtonDisabled =
			store.isPreviewLoading ||
			isSubmittingFeedback ||
			!store.currentResource ||
			!store.currentPreviewItem ||
			hasSubmittedFeedback;

		if (store.previewError) {
			return <ErrorState error={store.previewError} />;
		}

		if (!store.currentPreviewItem && !store.isPreviewLoading) {
			return <EmptyState />;
		}

		return (
			<Stack h="100%" spacing={0}>
				<Group className={classes.headerWrapper}>
					<Title size="sm">Preview output</Title>
					<Group className={classes.headerNavigationWrapper}>
						<Group className={classes.headerNavigationControls}>
							<IconButton
								variant="tertiary"
								iconName="chevronLeft"
								disabled={
									store.previewIndex === 0 ||
									store.isPreviewLoading ||
									!store.currentResource
								}
								onClick={decrementIndex}
							/>
							<IconButton
								variant="tertiary"
								iconName="chevronRight"
								disabled={
									store.previewIndex === resourceCount - 1 ||
									store.isPreviewLoading ||
									!store.currentResource
								}
								onClick={incrementIndex}
							/>
						</Group>
						<Text
							size="sm"
							color={
								store.isPreviewLoading
									? 'text/secondary/default'
									: 'text/primary/default'
							}
						>
							{resourceCount === 0
								? '0 previews'
								: `${store.previewIndex + 1} of ${resourceCount} previews`}
						</Text>
						<Group />
					</Group>
				</Group>
				<Stack className={classes.contentWrapper} style={{ flex: 1 }}>
					{store.isPreviewLoading && <LoadingState />}
					{!store.isPreviewLoading &&
						store.currentResource &&
						store.currentPreviewItem && (
							<Preview
								resource={store.currentResource}
								data={store.currentPreviewItem}
								tagsData={tagsData}
							/>
						)}
				</Stack>
				<Group className={classes.footerWrapper}>
					<Text size="xs" weight="semibold">
						Is this your expected output?
					</Text>
					<Group className={classes.footerFeedbackControls}>
						<IconButton
							disabled={isButtonDisabled}
							variant="tertiary"
							iconName={
								currentFeedback === 'positive' ? 'thumbUpFilled' : 'thumbUp'
							}
							onClick={handlePositiveFeedback}
						/>
						<IconButton
							disabled={isButtonDisabled}
							variant="tertiary"
							iconName={
								currentFeedback === 'negative' ? 'thumbDownFilled' : 'thumbDown'
							}
							onClick={handleNegativeFeedback}
						/>
					</Group>
				</Group>
			</Stack>
		);
	}
);
